import React from 'react'
import {
  DcFlexBox,
  DcInput,
  PixelFactoryContext,
  PixelImage,
  PixelTable,
  PixelText,
} from 'pixel-base'
import type { AjaxError, AjaxResponse } from 'rxjs/ajax'
import {
  updateProductBom,
  deleteProductBom,
  type ProductBomItem,
  type BulkUploadResponse,
} from 'utils/api-routes-planning'
import { CheckIcon } from 'utils/icons/check-icon'
import { EditIcon } from 'utils/icons/edit-icon'
import { TrashIcon } from 'utils/icons/trash-icon'
import { XmarkIcon } from 'utils/icons/xmark-icon'
import { IPixelFactory } from '../../../../../decl.d'

interface BomTableProps {
  items: ProductBomItem[]
  onRefresh: () => void
  productId: number | string
}

const BomTable: React.FC<BomTableProps> = ({
  items,
  onRefresh,
  productId: _productId,
}) => {
  const { pixelConfirm } = React.useContext<IPixelFactory>(PixelFactoryContext)
  const [editingId, setEditingId] = React.useState<number | null>(null)
  const [editValues, setEditValues] = React.useState<{
    quantity_required?: number
    notes?: string
  }>({})
  const [isUpdating, setIsUpdating] = React.useState(false)
  const [deletingId, setDeletingId] = React.useState<number | null>(null)

  const handleEdit = (item: ProductBomItem) => {
    if (!item.id) return
    setEditingId(item.id)
    setEditValues({
      quantity_required: item.quantity_required ?? 0,
      notes: item.notes || '',
    })
  }

  const handleCancel = () => {
    setEditingId(null)
    setEditValues({})
  }

  const handleSave = (item: ProductBomItem) => {
    if (!item.id) return

    const updatePayload: {
      quantity_required?: number
      notes?: string
    } = {}

    if (editValues.quantity_required !== undefined) {
      updatePayload.quantity_required = editValues.quantity_required
    }
    if (editValues.notes !== undefined) {
      updatePayload.notes = editValues.notes
    }

    setIsUpdating(true)
    updateProductBom(item.id, updatePayload).subscribe({
      next: (_res: AjaxResponse<BulkUploadResponse>) => {
        setEditingId(null)
        setEditValues({})
        setIsUpdating(false)
        onRefresh()
      },
      error: (err: AjaxError) => {
        console.error('bom-update:error', err)
        setIsUpdating(false)
      },
    })
  }

  const handleDelete = (item: ProductBomItem) => {
    if (!item.id) return

    pixelConfirm({
      title: 'Delete BOM Component',
      message: `Are you sure you want to delete component "${
        item.component?.product_sku || item.component?.product_name
      }"?`,
      onConfirm: () => {
        setDeletingId(item.id!)
        deleteProductBom(item.id).subscribe({
          next: (_res: AjaxResponse<BulkUploadResponse>) => {
            setDeletingId(null)
            onRefresh()
          },
          error: (err: AjaxError) => {
            console.error('bom-delete:error', err)
            setDeletingId(null)
          },
        })
      },
    })
  }

  const columns = [
    {
      dataKey: 'component_name',
      name: 'Component Name',
      sortable: true,
      selector: (row: ProductBomItem) => (
        <DcFlexBox height="auto" gap="5px" alignItems="center">
          {row.component?.product_image_path && (
            <PixelImage
              src={row.component.product_image_path}
              alt={row.component.product_name}
              width="32px"
              height="32px"
            />
          )}
          <PixelText multiLine>{row.component?.product_name || '--'}</PixelText>
        </DcFlexBox>
      ),
    },
    {
      dataKey: 'component_sku',
      width: '10%',
      name: 'Component SKU',
      sortable: true,
      selector: (row: ProductBomItem) => row.component?.product_sku || '--',
    },
    {
      dataKey: 'quantity_required',
      width: '10%',
      name: 'Required Qty',
      sortable: true,
      selector: (row: ProductBomItem) => {
        const isEditing = row.id === editingId
        if (isEditing) {
          return (
            <DcInput
              inputSize="small"
              name="quantity_required"
              type="number"
              min={0}
              step="0.01"
              value={
                typeof editValues.quantity_required === 'number'
                  ? editValues.quantity_required
                  : ''
              }
              onChange={(e) => {
                const value = e.target.value
                setEditValues((prev) => ({
                  ...prev,
                  quantity_required:
                    value === '' ? undefined : Math.max(0, Number(value) || 0),
                }))
              }}
              style={{ width: '100px', fontSize: '12px' }}
            />
          )
        }
        return typeof row.quantity_required === 'number'
          ? row.quantity_required
          : '--'
      },
    },
    {
      dataKey: 'notes',
      width: '20%',
      name: 'Notes',
      sortable: false,
      selector: (row: ProductBomItem) => {
        const isEditing = row.id === editingId
        if (isEditing) {
          return (
            <DcInput
              inputSize="small"
              name="notes"
              type="text"
              value={editValues.notes || ''}
              onChange={(e) => {
                setEditValues((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }}
              placeholder="Optional notes"
              style={{ width: '200px', fontSize: '12px' }}
            />
          )
        }
        return row.notes || '--'
      },
    },
    {
      dataKey: 'actions',
      name: 'Actions',
      width: '150px',
      sortable: false,
      selector: (row: ProductBomItem) => {
        const isEditing = row.id === editingId
        const isDeleting = row.id === deletingId

        if (isEditing) {
          return (
            <DcFlexBox gap="8px" justifyContent="center">
              <CheckIcon
                color="#28a745"
                size={20}
                style={{ cursor: 'pointer' }}
                onClick={() => handleSave(row)}
              />
              <XmarkIcon
                color="#dc3545"
                size={20}
                style={{ cursor: 'pointer' }}
                onClick={handleCancel}
              />
            </DcFlexBox>
          )
        }

        return (
          <DcFlexBox gap="8px" justifyContent="center">
            <EditIcon
              color="#5c4dff"
              size={20}
              style={{ cursor: 'pointer' }}
              onClick={() => handleEdit(row)}
            />
            <TrashIcon
              color="#dc3545"
              size={20}
              style={{
                cursor: isDeleting ? 'not-allowed' : 'pointer',
                opacity: isDeleting ? 0.5 : 1,
              }}
              onClick={() => !isDeleting && handleDelete(row)}
            />
          </DcFlexBox>
        )
      },
    },
  ]

  return (
    <PixelTable
      columns={columns}
      data={items || []}
      isSearchable={true}
      fixedHeader
      fixedHeaderScrollHeight="calc(100vh - 400px)"
      pagination={items.length > 10}
      paginationPerPage={10}
      progressPending={isUpdating || deletingId !== null}
      textSize="small"
    />
  )
}

export default BomTable
